import { useState } from "react";
import { Icon, Avatar, Modal, Field, Inp, Sel, Txt, SaveBtn, inputStyle } from "../shared/ui.jsx";
import { USER_COLORS } from "../shared/constants.js";

const PROJECTS = ["Litvill Lessons", "Litvill Lab", "Both"];
const PLACES = ["House", "House garden", "Litvill 1", "Litvill 2", "India", "Other"];
const QUARTERS = ["—", "Q1", "Q2", "Q3", "Q4"];
const STATUSES = ["Not started", "In progress", "Done"];
const STATUS_COLORS = {
  "Not started": "#8a7a60",
  "In progress": "#3d6b8a",
  "Done": "#4a8a6a",
};

function currentYear() { return new Date().getFullYear(); }

export function GoalForm({ onSave, onClose, existing, user }) {
  const [form, setForm] = useState(existing || {
    description: "", project: "Litvill Lessons", place: "House",
    year: currentYear(), quarter: "—",
    status: "Not started", notes: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = () => {
    if (!form.description.trim()) return;
    onSave({
      ...form,
      id: existing?.id || Date.now(),
      createdBy: existing?.createdBy || user,
      createdAt: existing?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      doneAt: form.status === "Done" ? (existing?.doneAt || new Date().toISOString()) : null,
    });
    onClose();
  };
  return (
    <>
      <Field label="Goal *"><Inp value={form.description} onChange={e => set("description", e.target.value)} placeholder="Clear the weeds, Sign MoU with X University…" /></Field>
      <Field label="Place"><Sel value={form.place} onChange={e => set("place", e.target.value)}>{PLACES.map(p => <option key={p}>{p}</option>)}</Sel></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Field label="Project"><Sel value={form.project} onChange={e => set("project", e.target.value)}>{PROJECTS.map(p => <option key={p}>{p}</option>)}</Sel></Field>
        <Field label="Status"><Sel value={form.status} onChange={e => set("status", e.target.value)}>{STATUSES.map(s => <option key={s}>{s}</option>)}</Sel></Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Field label="Year"><Inp type="number" value={form.year} onChange={e => set("year", Number(e.target.value))} /></Field>
        <Field label="Target quarter (optional)"><Sel value={form.quarter} onChange={e => set("quarter", e.target.value)}>{QUARTERS.map(q => <option key={q}>{q}</option>)}</Sel></Field>
      </div>
      <Field label="Notes"><Txt value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Context, related tasks, blockers…" /></Field>
      <SaveBtn onClick={save} />
    </>
  );
}

function GoalCard({ goal, onEdit, onDelete, onToggleDone, currentUser, linkedCount }) {
  const isOwn = goal.createdBy === currentUser;
  const isDone = goal.status === "Done";
  const statusColor = STATUS_COLORS[goal.status] || "var(--ink50)";
  const projectColor = goal.project === "Litvill Lessons" ? "#3d6b8a" : goal.project === "Litvill Lab" ? "#5b8a6b" : "#7a5c2e";
  const userColor = USER_COLORS[goal.createdBy] || "var(--accent)";

  return (
    <div style={{
      padding: "0.85rem 1rem", background: "var(--paper-dark)", borderRadius: 4, marginBottom: "0.5rem",
      border: "1px solid var(--ink10)", borderLeft: `3px solid ${statusColor}`,
      opacity: isDone ? 0.65 : 1,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.08em", color: projectColor, fontWeight: 600 }}>{goal.project}</span>
            {goal.place && <span style={{ fontSize: "0.65rem", color: "var(--ink50)" }}>· 📍 {goal.place}</span>}
            {goal.quarter && goal.quarter !== "—" && <span style={{ fontSize: "0.65rem", color: "var(--ink30)" }}>· {goal.quarter}</span>}
          </div>
          <div style={{ fontFamily: "var(--font-head)", fontSize: "0.98rem", color: "var(--ink)", textDecoration: isDone ? "line-through" : "none" }}>
            {goal.description}
          </div>
          {goal.notes && <div style={{ fontSize: "0.78rem", color: "var(--ink50)", marginTop: 4, fontStyle: "italic" }}>{goal.notes}</div>}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: 6 }}>
            <Avatar user={goal.createdBy} size={14} />
            <span style={{ fontSize: "0.68rem", color: userColor }}>{goal.createdBy}</span>
            <span style={{ fontSize: "0.7rem", padding: "1px 7px", borderRadius: 10, background: statusColor + "22", color: statusColor, fontWeight: 600 }}>{goal.status}</span>
            {linkedCount && linkedCount.total > 0 && (
              <span style={{ fontSize: "0.7rem", color: "var(--ink30)" }}>· {linkedCount.done}/{linkedCount.total} tasks</span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.4rem", flexShrink: 0 }}>
          {isOwn && (
            <button onClick={() => onToggleDone(goal)} title={isDone ? "Mark not done" : "Mark done"} style={{
              width: 26, height: 26, borderRadius: "50%", border: `2px solid ${isDone ? "#4a8a6a" : "var(--ink20)"}`,
              background: isDone ? "#4a8a6a" : "transparent", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: isDone ? "white" : "var(--ink30)",
            }}><Icon name="check" size={12} /></button>
          )}
          <div style={{ display: "flex", gap: 2 }}>
            {isOwn && <button onClick={() => onEdit(goal)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink30)", padding: 2 }}><Icon name="edit" size={13} /></button>}
            {isOwn && <button onClick={() => onDelete(goal.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink30)", padding: 2 }}><Icon name="trash" size={13} /></button>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GoalsTab({ user, goals, setGoals, searchQ, todos = [] }) {
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filterProject, setFilterProject] = useState("all");
  const [filterPlace, setFilterPlace] = useState("all");
  const [filterYear, setFilterYear] = useState(currentYear());
  const [hideDone, setHideDone] = useState(false);

  let items = goals || [];
  if (searchQ) items = items.filter(g => g.description.toLowerCase().includes(searchQ) || (g.notes || "").toLowerCase().includes(searchQ));
  if (filterProject !== "all") items = items.filter(g => g.project === filterProject);
  if (filterPlace !== "all") items = items.filter(g => g.place === filterPlace);
  items = items.filter(g => g.year === filterYear);
  if (hideDone) items = items.filter(g => g.status !== "Done");

  // Sort: not-done first, then by quarter (unset last within each group)
  const qOrder = { "Q1": 0, "Q2": 1, "Q3": 2, "Q4": 3, "—": 4 };
  items = [...items].sort((a, b) => {
    if (a.status === "Done" && b.status !== "Done") return 1;
    if (a.status !== "Done" && b.status === "Done") return -1;
    return (qOrder[a.quarter] ?? 4) - (qOrder[b.quarter] ?? 4);
  });

  const save = (goal) => {
    if (editItem) setGoals((goals || []).map(g => g.id === goal.id ? goal : g));
    else setGoals([...(goals || []), goal]);
    setEditItem(null); setShowForm(false);
  };
  const del = (id) => setGoals((goals || []).filter(g => g.id !== id));
  const toggleDone = (goal) => {
    const newStatus = goal.status === "Done" ? "In progress" : "Done";
    setGoals((goals || []).map(g => g.id === goal.id
      ? { ...g, status: newStatus, doneAt: newStatus === "Done" ? new Date().toISOString() : null }
      : g
    ));
  };

  const doneCount = (goals || []).filter(g => g.year === filterYear && g.status === "Done").length;
  const totalCount = (goals || []).filter(g => g.year === filterYear).length;

  return (
    <div>
      {/* Year + progress */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={() => setFilterYear(y => y - 1)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink50)", fontSize: "1.1rem", padding: "0 4px" }}>‹</button>
        <span style={{ fontFamily: "var(--font-head)", fontSize: "1.1rem", color: "var(--accent)" }}>{filterYear}</span>
        <button onClick={() => setFilterYear(y => y + 1)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink50)", fontSize: "1.1rem", padding: "0 4px" }}>›</button>
        {totalCount > 0 && (
          <span style={{ fontSize: "0.78rem", color: "var(--ink50)", marginLeft: "0.5rem" }}>
            {doneCount} / {totalCount} done
          </span>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
        <Sel value={filterProject} onChange={e => setFilterProject(e.target.value)} style={{ ...inputStyle, width: "auto", fontSize: "0.8rem" }}>
          <option value="all">All projects</option>
          {PROJECTS.map(p => <option key={p}>{p}</option>)}
        </Sel>
        <Sel value={filterPlace} onChange={e => setFilterPlace(e.target.value)} style={{ ...inputStyle, width: "auto", fontSize: "0.8rem" }}>
          <option value="all">All places</option>
          {PLACES.map(p => <option key={p}>{p}</option>)}
        </Sel>
        <button onClick={() => setHideDone(v => !v)} style={{
          fontSize: "0.8rem", padding: "0.35rem 0.7rem", borderRadius: 3, cursor: "pointer",
          border: `1px solid ${hideDone ? "var(--accent)" : "var(--ink20)"}`,
          background: hideDone ? "var(--accent)" : "transparent",
          color: hideDone ? "var(--paper)" : "var(--ink50)",
        }}>{hideDone ? "Showing active only" : "Hide done"}</button>
      </div>

      <div style={{ fontSize: "0.78rem", color: "var(--ink50)", marginBottom: "0.5rem" }}>{items.length} goal(s) · shared</div>
      {items.length === 0 && <p style={{ color: "var(--ink30)", fontStyle: "italic", fontSize: "0.9rem" }}>No goals for {filterYear}.</p>}

      {items.map(g => {
        const linked = todos.filter(t => String(t.goalId) === String(g.id));
        const linkedCount = { total: linked.length, done: linked.filter(t => t.status === "done").length };
        return (
          <GoalCard key={g.id} goal={g} currentUser={user} linkedCount={linkedCount}
            onEdit={item => { setEditItem(item); setShowForm(true); }}
            onDelete={del}
            onToggleDone={toggleDone}
          />
        );
      })}

      {showForm && (
        <Modal title={editItem ? "Edit Goal" : "New Goal"} onClose={() => { setShowForm(false); setEditItem(null); }}>
          <GoalForm user={user} onSave={save} onClose={() => { setShowForm(false); setEditItem(null); }} existing={editItem} />
        </Modal>
      )}
    </div>
  );
}